import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

// Keyframes d'animation
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-13.5px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { transform: scale(0.9); }
  50% { transform: scale(0.945); }
  100% { transform: scale(0.9); }
`;

const lightSweep = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

// Composant principal Hero
const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 0 5%;
  padding-top: 3rem;
  background: ${props => props.theme.name === 'light'
    ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    : props.theme.colors.background};
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 2rem 5%;
    flex-direction: column;
    justify-content: center;
    text-align: center;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 63%;
    height: 100%;
    background: radial-gradient(circle, rgba(94, 23, 235, ${props => props.theme.name === 'light' ? '0.05' : '0.15'}) 0%, transparent 70%);
    z-index: 0;
    
    @media (max-width: 768px) {
      width: 100%;
      opacity: 0.5;
    }
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 45%;
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 991px) {
    max-width: 60%;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    margin-bottom: 2rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.2rem;
  font-weight: 700;
  margin-bottom: 1.35rem;
  line-height: 1.2;
  color: ${props => props.theme.colors.text};
  
  span {
    color: ${props => props.theme.colors.accent};
  }
  
  @media (max-width: 991px) {
    font-size: 3rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.25rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 0.99rem;
  margin-bottom: 2.25rem;
  color: ${props => props.theme.colors.secondaryText};
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.8rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 0.9rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.9rem;
    align-items: center;
  }
`;

const PrimaryButton = styled.a`
  background: ${props => props.theme.colors.accentGradient};
  color: white;
  padding: 0.9rem 1.8rem;
  border-radius: 45px;
  font-weight: 600;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  animation: ${pulse} 2s infinite;
  text-decoration: none;
  display: inline-block;
  text-align: center;
  
  @media (max-width: 768px) {
    width: 80%;
    max-width: 300px;
  }
  
  &:hover {
    transform: translateY(-2.7px);
    box-shadow: 0 9px 18px rgba(94, 23, 235, 0.3);
  }
  
  &:focus {
    outline: 2.7px solid ${props => props.theme.colors.accent};
    outline-offset: 1.8px;
  }
`;

const SecondaryButton = styled.a`
  background: transparent;
  color: ${props => props.theme.colors.accent};
  padding: 0.9rem 1.8rem;
  border-radius: 45px;
  font-weight: 600;
  border: 1.8px solid ${props => props.theme.colors.accent};
  transition: all 0.3s ease;
  font-size: 0.9rem;
  text-decoration: none;
  display: inline-block;
  text-align: center;
  cursor: pointer;
  
  @media (max-width: 768px) {
    width: 80%;
    max-width: 300px;
  }
  
  &:hover {
    background: rgba(94, 23, 235, 0.1);
    transform: translateY(-2.7px);
  }
  
  &:focus {
    outline: 2.7px solid ${props => props.theme.colors.accent};
    outline-offset: 1.8px;
  }
`;

// Animation 3D
const Interactive3DContainer = styled.div`
  position: absolute;
  top: 22%;
  right: 8%;
  transform: translateY(-50%);
  width: 36%;
  height: 58.5%;
  perspective: 1080px;
  z-index: 1;
  cursor: grab;
  animation: ${float} 6s ease-in-out infinite;
  will-change: transform;
  
  &:active {
    cursor: grabbing;
  }
  
  @media (max-width: 991px) {
    display: none;
  }
`;

const MockupWrapper = styled.div.attrs(props => ({
  style: {
    transform: `rotateX(${props.rotateX}deg) rotateY(${props.rotateY}deg)`
  },
  role: 'presentation'
}))`
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.15s ease-out;
  will-change: transform;
`;

const DeviceShadow = styled.div`
  position: absolute;
  width: 81%;
  height: 18px;
  bottom: -36px;
  left: 5%;
  background: rgba(0, 0, 0, 0.15);
  filter: blur(13.5px);
  border-radius: 50%;
  transform: scale(0.9, 0.27);
  opacity: 0.7;
  animation: ${float} 6s ease-in-out infinite reverse;
`;

const DeviceContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
`;

const DeviceFrame = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: ${props => props.theme.name === 'light' ? '#e0e0e0' : '#2a2e3a'};
  border-radius: 18px;
  box-shadow: 0 27px 54px rgba(0, 0, 0, 0.3);
  transform: translateZ(-9px);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 9px;
    left: 50%;
    transform: translateX(-50%);
    width: 5%;
    height: 7.2px;
    background: ${props => props.theme.name === 'light' ? '#d0d0d0' : '#1a1c24'};
    border-radius: 3.6px;
    z-index: 4;
    display: none;
  }
`;

const DeviceScreen = styled.div`
  position: relative;
  width: calc(100% - 14.4px);
  height: calc(100% - 14.4px);
  margin: 7.2px;
  background: ${props => props.theme.name === 'light' ? '#ffffff' : '#1a1c24'};
  border-radius: 10.8px;
  overflow: hidden;
  box-shadow: inset 0 0 9px rgba(0, 0, 0, 0.1);
`;

// Autres composants de mockup...
const MockupBase = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  transform: translateZ(0px);
`;

const MockupHeader = styled.div`
  height: 45px;
  background-color: ${props => props.theme.name === 'light' ? '#f8f9fa' : '#1a1c24'};
  border-bottom: 0.9px solid ${props => props.theme.name === 'light' ? '#e9ecef' : '#2a2e3a'};
  display: flex;
  align-items: center;
  padding: 0 14.4px;
  transform: translateZ(4.5px);
`;

const MockupLogo = styled.div`
  width: 90px;
  height: 22.5px;
  background: ${props => props.theme.colors.accentGradient};
  border-radius: 4.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::after {
    content: 'Rankeed';
    color: white;
    font-size: 10.8px;
    font-weight: bold;
  }
`;

const MockupNav = styled.div`
  display: flex;
  margin-left: auto;
  gap: 10.8px;
`;

const MockupNavItem = styled.div`
  width: 36px;
  height: 7.2px;
  background-color: ${props => props.theme.name === 'light' ? '#e0e0e0' : '#2a2e3a'};
  border-radius: 3.6px;
`;

const MockupHeroArea = styled.div`
  height: 162px;
  background: ${props => props.theme.name === 'light'
    ? 'linear-gradient(145deg, #f0f2f5, #e9ecef)'
    : 'linear-gradient(145deg, #1e2028, #191c24)'};
  display: flex;
  padding: 14.4px;
  transform: translateZ(9px);
  position: relative;
  overflow: hidden;
`;

const LightEffect = styled.div`
  position: absolute;
  width: 150%;
  height: 100%;
  top: 0;
  left: -25%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: ${lightSweep} 10s ease-in-out infinite alternate;
  transform-origin: center;
  pointer-events: none;
  z-index: 5;
`;

const MockupHeroContent = styled.div`
  flex: 1;
  padding: 14.4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MockupHeroTitle = styled.div`
  width: 70%;
  height: 16.2px;
  background-color: ${props => props.theme.name === 'light' ? '#d9d9d9' : '#2a2e3a'};
  margin-bottom: 10.8px;
  border-radius: 3.6px;
`;

const MockupHeroText = styled.div`
  width: 90%;
  height: 5.4px;
  background-color: ${props => props.theme.name === 'light' ? '#e0e0e0' : '#353945'};
  margin-bottom: 5.4px;
  border-radius: 2.7px;
  
  &:nth-child(3) {
    width: 80%;
  }
  
  &:nth-child(4) {
    width: 60%;
  }
`;

const MockupHeroButton = styled.div`
  width: 81px;
  height: 22.5px;
  background-color: ${props => props.theme.colors.accent};
  border-radius: 10.8px;
  margin-top: 13.5px;
`;

const MockupHeroImage = styled.div`
  width: 38%;
  background: ${props => props.theme.name === 'light'
    ? 'linear-gradient(145deg, #d9d9d9, #e9ecef)'
    : 'linear-gradient(145deg, #232530, #2a2e3a)'};
  border-radius: 7.2px;
  transform: translateZ(13.5px);
  position: relative;
  overflow: hidden;
`;

const MockupSectionTitle = styled.div`
  width: 135px;
  height: 14.4px;
  background-color: ${props => props.theme.name === 'light' ? '#d9d9d9' : '#2a2e3a'};
  margin: 18px auto;
  border-radius: 3.6px;
  transform: translateZ(9px);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -7.2px;
    left: 50%;
    transform: translateX(-50%);
    width: 27px;
    height: 2.7px;
    background: ${props => props.theme.colors.accent};
    border-radius: 1.35px;
  }
`;

const MockupCardsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 14.4px;
  transform: translateZ(18px);
`;

const MockupCard = styled.div`
  width: 30%;
  height: 108px;
  background: ${props => props.theme.name === 'light'
    ? 'linear-gradient(145deg, #ffffff, #f8f9fa)'
    : 'linear-gradient(145deg, #232530, #1e2028)'};
  border-radius: 9px;
  box-shadow: 0 4.5px 13.5px rgba(0, 0, 0, 0.1);
  padding: 10.8px;
  transform: ${props => `translateZ(${props.elevation * 0.9}px)`};
  will-change: transform;
  position: relative;
  overflow: hidden;
`;

const CardLightEffect = styled.div`
  position: absolute;
  width: 200%;
  height: 100%;
  top: 0;
  left: -50%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.02) 25%,
    rgba(255, 255, 255, 0.06) 50%,
    rgba(255, 255, 255, 0.02) 75%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: ${lightSweep} 10s ease-in-out infinite alternate;
  transform-origin: center;
  pointer-events: none;
  z-index: 5;
`;

const MockupCardIcon = styled.div`
  width: 27px;
  height: 27px;
  border-radius: 7.2px;
  background: ${props => props.theme.colors.accentGradient};
  margin-bottom: 10.8px;
`;

const MockupCardTitle = styled.div`
  width: 80%;
  height: 7.2px;
  background-color: ${props => props.theme.name === 'light' ? '#d9d9d9' : '#2a2e3a'};
  margin-bottom: 7.2px;
  border-radius: 2.7px;
`;

const MockupCardText = styled.div`
  width: 90%;
  height: 4.5px;
  background-color: ${props => props.theme.name === 'light' ? '#e0e0e0' : '#353945'};
  margin-bottom: 4.5px;
  border-radius: 1.8px;
  
  &:nth-child(4) {
    width: 70%;
  }
`;

// Lien d'accessibilité
const SkipToContent = styled.a`
  position: absolute;
  top: -9999px;
  left: 0;
  padding: 7.2px;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  z-index: 100;
  
  &:focus {
    top: 0;
    position: fixed;
  }
`;

const ScrollIndicator = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: absolute;
    bottom: 5%;
    left: 50%;
    transform: translateX(-50%);
    color: ${props => props.theme.colors.secondaryText};
    opacity: 0.7;
    width: 100%;
    padding-bottom: 18px;
  }
`;

const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% { 
    transform: translateY(0) rotate(45deg); 
  } 
  40% { 
    transform: translateY(-10px) rotate(45deg); 
  } 
  60% { 
    transform: translateY(-5px) rotate(45deg); 
  }
`;

const ScrollArrow = styled.div`
  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    border-right: 2px solid ${props => props.theme.colors.accent};
    border-bottom: 2px solid ${props => props.theme.colors.accent};
    animation: ${bounceAnimation} 2s infinite;
    margin-top: 9px;
  }
`;

const ScrollText = styled.span`
  @media (max-width: 768px) {
    font-size: 0.72rem;
    margin-bottom: 9px;
  }
`;

// On ne crée pas de version alternative pour mobile

const Hero = () => {
  const defaultRotation = { x: 4.5, y: -13.5 };
  const [rotation, setRotation] = useState(defaultRotation);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const requestRef = useRef();
  const previousTimeRef = useRef();

  const containerRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const rotateY = ((e.clientX - centerX) / (rect.width / 2)) * 7.2 - 13.5;
    const rotateX = -((e.clientY - centerY) / (rect.height / 2)) * 7.2 + 4.5;

    setRotation({ x: rotateX, y: rotateY });
  };

  const handleMouseEnter = () => {
    setIsMouseOver(true);
  };

  const handleMouseLeave = () => {
    setIsMouseOver(false);
  };

  const animate = (time) => {
    if (previousTimeRef.current !== undefined) {
      if (!isMouseOver) {
        const dampingFactor = 0.04;
        const targetX = defaultRotation.x;
        const targetY = defaultRotation.y;

        const dx = targetX - rotation.x;
        const dy = targetY - rotation.y;

        setRotation(prev => ({
          x: prev.x + dx * dampingFactor,
          y: prev.y + dy * dampingFactor
        }));
      }
    }

    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setRotation(defaultRotation);
      return;
    }

    let animationId;

    if (!isMouseOver) {
      let angle = 0;
      const floatAnimation = () => {
        angle += 0.009;
        const floatY = Math.sin(angle) * 1.8;
        const floatX = Math.cos(angle) * 1.8;

        setRotation({
          x: defaultRotation.x + floatX,
          y: defaultRotation.y + floatY
        });

        animationId = requestAnimationFrame(floatAnimation);
      };

      animationId = requestAnimationFrame(floatAnimation);
    } else {
      requestRef.current = requestAnimationFrame(animate);
    }

    return () => {
      cancelAnimationFrame(animationId);
      cancelAnimationFrame(requestRef.current);
    };
  }, [isMouseOver]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (mediaQuery.matches) {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
      setRotation(defaultRotation);
    }

    const handleChange = (e) => {
      if (e.matches) {
        if (requestRef.current) {
          cancelAnimationFrame(requestRef.current);
        }
        setRotation(defaultRotation);
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return (
    <HeroSection id="accueil" role="banner" aria-label="Accueil">
      <SkipToContent href="#services">
        Aller au contenu principal
      </SkipToContent>

      <HeroContent>
        <HeroTitle>
          Créateurs de sites web <span>professionnels</span> et impactants
        </HeroTitle>
        <HeroSubtitle>
          Notre agence conçoit et développe des sites vitrines élégants et performants qui transforment votre vision en une présence web remarquable.
        </HeroSubtitle>
        <HeroButtons>
          <PrimaryButton
            href="#contact"
            aria-label="Discuter de votre projet de site web"
          >
            Discuter de votre projet
          </PrimaryButton>
          <SecondaryButton
            href="#services"
            aria-label="Découvrir nos services de création de sites web"
          >
            Découvrir nos services
          </SecondaryButton>


        </HeroButtons>


      </HeroContent>

      {/* Mobile Scroll Indicator */}
      <ScrollIndicator>
        <ScrollText>Faites défiler</ScrollText>
        <ScrollArrow />
      </ScrollIndicator>

      {/* Version desktop du mockup 3D */}
      <Interactive3DContainer
        id="interactive-mockup"
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-hidden="true"
      >
        <MockupWrapper rotateX={rotation.x} rotateY={rotation.y}>
          <DeviceContainer>
            <DeviceFrame>
              <DeviceScreen>
                <MockupBase>
                  <MockupHeader>
                    <MockupLogo />
                    <MockupNav>
                      <MockupNavItem />
                      <MockupNavItem />
                      <MockupNavItem />
                    </MockupNav>
                  </MockupHeader>

                  <MockupHeroArea>
                    <LightEffect />
                    <MockupHeroContent>
                      <MockupHeroTitle />
                      <MockupHeroText />
                      <MockupHeroText />
                      <MockupHeroText />
                      <MockupHeroButton />
                    </MockupHeroContent>
                    <MockupHeroImage />
                  </MockupHeroArea>

                  <MockupSectionTitle />

                  <MockupCardsContainer>
                    <MockupCard elevation={25}>
                      <CardLightEffect />
                      <MockupCardIcon />
                      <MockupCardTitle />
                      <MockupCardText />
                      <MockupCardText />
                    </MockupCard>
                    <MockupCard elevation={30}>
                      <CardLightEffect />
                      <MockupCardIcon />
                      <MockupCardTitle />
                      <MockupCardText />
                      <MockupCardText />
                    </MockupCard>
                    <MockupCard elevation={35}>
                      <CardLightEffect />
                      <MockupCardIcon />
                      <MockupCardTitle />
                      <MockupCardText />
                      <MockupCardText />
                    </MockupCard>
                  </MockupCardsContainer>
                </MockupBase>
              </DeviceScreen>
            </DeviceFrame>
          </DeviceContainer>
        </MockupWrapper>
        <DeviceShadow />
      </Interactive3DContainer>

      {/* Pas de version mobile du mockup */}
    </HeroSection>
  );
};

export default Hero;